/**
 * RBAC no frontend (S3.6) — espelha backend/app/api/capabilities.py.
 *
 * NÃO é segurança (o backend é a fonte da verdade e já retorna 403). Aqui é só
 * UX: esconder/desabilitar ações que a role atual não pode executar.
 *
 * Role efetiva = role da sessão (login) OU, em acesso de bancada (ADMIN_TOKEN
 * sem sessão), super_admin (platform admin).
 */
import { isRecoveryActive, loadSession } from "./auth.js";
import { loadConfig } from "./config.js";
import { ALL_NAV_ITEMS } from "../nav/navConfig.js";

// Capabilities — strings IDÊNTICAS às do backend (capabilities.py).
export const CAP = {
  DEVICES_READ: "devices:read",
  DEVICES_COMMAND: "devices:command",
  DEVICES_CONFIG: "devices:config",
  DEVICES_OTA: "devices:ota",
  SCHEDULER_WRITE: "scheduler:write",
  WATCHDOG_WRITE: "watchdog:write",
  INVENTORY_WRITE: "inventory:write",
  FLEET_WRITE: "fleet:write",
  // Pseudo-capability só de plataforma (tenants/global) — apenas super_admin.
  PLATFORM: "platform",
};

// role -> Set de capabilities. super_admin NÃO entra aqui: faz bypass (tudo).
const ROLE_CAPS = {
  viewer: new Set([CAP.DEVICES_READ]),
  field: new Set([CAP.DEVICES_READ]),
  noc: new Set([CAP.DEVICES_READ, CAP.DEVICES_COMMAND]),
  tenant_admin: new Set([
    CAP.DEVICES_READ,
    CAP.DEVICES_COMMAND,
    CAP.DEVICES_CONFIG,
    CAP.DEVICES_OTA,
    CAP.SCHEDULER_WRITE,
    CAP.WATCHDOG_WRITE,
    CAP.INVENTORY_WRITE,
    CAP.FLEET_WRITE,
  ]),
};

/** Role efetiva da sessão atual (ou super_admin em bancada), ou null. */
export function currentRole() {
  const s = loadSession();
  if (s && s.role) return s.role;
  try {
    // SAAS-AUTH-BEARER-ENFORCEMENT — ADMIN_TOKEN só implica super_admin em
    // recovery explícito (sem recovery, NÃO concede platform-admin na UX).
    if (loadConfig().adminToken && isRecoveryActive()) return "super_admin";
  } catch {
    /* noop */
  }
  return null;
}

/** true se a role atual é super_admin (ou bancada ADMIN_TOKEN). */
export function isPlatformAdmin() {
  return currentRole() === "super_admin";
}

/** true se a role atual é uma das informadas. Ex.: hasRole("tenant_admin","super_admin"). */
export function hasRole(...roles) {
  const r = currentRole();
  return !!r && roles.includes(r);
}

/** true se a role atual concede a capability (super_admin sempre true). */
export function hasCapability(capability) {
  const r = currentRole();
  if (!r) return false;
  if (r === "super_admin") return true; // bypass de plataforma
  if (capability === CAP.PLATFORM) return false; // só super_admin
  const caps = ROLE_CAPS[r];
  return caps ? caps.has(capability) : false;
}

/** Alias curto. */
export const can = hasCapability;

/**
 * S3.9E — visibilidade de um item de navegação para a role atual.
 * UX apenas (o backend é a verdade). Itens com `role`/`cap` que a role
 * não satisfaz ficam ocultos. Item ausente/sem gate = visível.
 */
export function navItemVisible(item) {
  if (!item) return true;
  if (item.role && !hasRole(item.role)) return false;
  if (item.cap && !hasCapability(item.cap)) return false;
  return true;
}

/**
 * S3.9E — true se a `view` (por nome) é permitida à role atual. Resolve o
 * item de nav correspondente; views fora do nav (ex.: "detail") = permitidas.
 */
export function viewAllowed(viewName) {
  const it = ALL_NAV_ITEMS.find((i) => i.view === viewName);
  return navItemVisible(it);
}

/**
 * S3.9E — landing default por role. super_admin / bancada (ADMIN_TOKEN) caem
 * no Dashboard (platform-only); tenant users caem em Saúde da Frota (`health`),
 * que é tenant-scoped e funciona para viewer/noc/tenant_admin.
 */
export function defaultViewForRole(role) {
  const r = role || currentRole();
  return r === "super_admin" ? "dashboard" : "health";
}
